import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template170: Downtown 2 - Ghirardelli, Pier 39, Musée Mécanique, Fisherman's Wharf. Todo desde backend (page).
// page.texts[0]=header | page.images | page.canvasImages (logo) | page.sections
const Template170 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const isSmallScreen = useWindowSize().width <= 600;
	const contentColor = textColor || "#444";

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const logoImage = getCanvasImageUrl("logo");
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];

	const actualHeaderColor = headerBackgroundColor || "#F2B705";
	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		marginBottom: 0.5,
		margin: 0,
	};
	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 9999,
					}}
				>
					{headerText}
					{logoImage && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: isSmallScreen ? 66 : 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					flexDirection: "column",
					height: "100%",
					flex: 1,
				}}
			>
				<Box data-block-id="text-1" sx={{ padding: "10px 12px", flex: 1 }}>
					<Box
						sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}
					>
						{/* Sección 0: Ghirardelli */}
						{sections.length > 0 && sections[0].title && (
							<Box
								data-block-id="text-2"
								sx={{
									display: "flex",
									gap: 2.5,
									alignItems: "flex-start",
									position: "relative",
								}}
							>
								{sections[0].imageId && getImageSrc(sections[0].imageId) && (
									<Box
										component="img"
										src={getImageSrc(sections[0].imageId)}
										alt=""
										sx={{
											width: "40%",
											minWidth: 140,
											height: 110,
											borderRadius: "12px",
											objectFit: "cover",
										}}
									/>
								)}
								{sections[0].sabiasQue?.[0] && (
									<Box sx={{ position: "absolute", top: 0, left: "33%" }}>
										<SabiasQue
											icono={sections[0].sabiasQue[0].icono}
											texto={sections[0].sabiasQue[0].texto}
										/>
									</Box>
								)}
								<Box sx={{ width: "60%" }}>
									<Box component="h3" sx={{ ...estiloH3, marginTop: 1 }}>
										{sections[0].title}
									</Box>
									{sections[0].body && (
										<Box component="p" sx={estiloP}>
											{sections[0].body}
										</Box>
									)}
								</Box>
							</Box>
						)}

						{/* Sección 1: Pier 39 */}
						{sections.length > 1 && sections[1].title && (
							<Box data-block-id="text-3" sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}>
								{sections[1].imageId && getImageSrc(sections[1].imageId) && (
									<Box
										component="img"
										src={getImageSrc(sections[1].imageId)}
										alt=""
										sx={{
											width: "40%",
											minWidth: 140,
											height: 110,
											borderRadius: "12px",
											objectFit: "cover",
										}}
									/>
								)}
								<Box sx={{ width: "60%" }}>
									<Box component="h3" sx={{ ...estiloH3, marginTop: 1 }}>
										{sections[1].title}
									</Box>
									{sections[1].body && (
										<Box component="p" sx={estiloP}>
											{sections[1].body}
										</Box>
									)}
								</Box>
							</Box>
						)}
					</Box>

					{/* Sección 2: Musée Mécanique (box con stickers y tooltips) */}
					{sections.length > 2 &&
						sections[2].layout === "box" &&
						sections[2].title && (
							<Box
								data-block-id="text-4"
								sx={{
									backgroundColor: "#fbf0cf",
									padding: 1.5,
									borderRadius: "12px",
									display: "flex",
									gap: 2,
									alignItems: "center",
									position: "relative",
									marginTop: 2.5,
								}}
							>
								{sections[2].boxLeftImageId &&
									getImageSrc(sections[2].boxLeftImageId) && (
										<Box
											component="img"
											src={getImageSrc(sections[2].boxLeftImageId)}
											alt=""
											sx={{
												width: 120,
												position: "absolute",
												bottom: -120,
												left: -20,
											}}
										/>
									)}
								{sections[2].boxLeftTooltip && (
									<Box
										sx={{
											position: "absolute",
											bottom: -100,
											left: 75,
											backgroundColor: "#fff8db",
											border: "2px solid #f5c400",
											borderRadius: "16px 16px 16px 0px",
											padding: "10px 12px",
											fontSize: 11,
											color: contentColor,
											boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
											maxWidth: 200,
										}}
										dangerouslySetInnerHTML={{
											__html: sections[2].boxLeftTooltip,
										}}
									/>
								)}
								{sections[2].boxRightImageId &&
									getImageSrc(sections[2].boxRightImageId) && (
										<Box
											component="img"
											src={getImageSrc(sections[2].boxRightImageId)}
											alt=""
											sx={{
												width: 120,
												position: "absolute",
												bottom: isSmallScreen ? -220 : -130,
												right: -20,
											}}
										/>
									)}
								{sections[2].boxRightTooltip && (
									<Box
										sx={{
											position: "absolute",
											bottom: isSmallScreen ? -200 : -70,
											right: 75,
											backgroundColor: "#fff8db",
											border: "2px solid #f5c400",
											borderRadius: isSmallScreen
												? "16px 16px 0px 16px"
												: "16px 16px 0px 16px",
											padding: "10px 12px",
											fontSize: 11,
											color: contentColor,
											boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
											maxWidth: isSmallScreen ? 100 : 160,
										}}
										dangerouslySetInnerHTML={{
											__html: sections[2].boxRightTooltip,
										}}
									/>
								)}
								<Box sx={{ paddingLeft: 1 }}>
									<Box component="h3" sx={estiloH3}>
										{sections[2].title}
									</Box>
									{sections[2].body && (
										<Box component="p" sx={estiloP}>
											{sections[2].body}
										</Box>
									)}
								</Box>
								{sections[2].sabiasQue?.[0] && (
									<Box sx={{ position: "absolute", top: 0, right: "40%" }}>
										<SabiasQue
											icono={sections[2].sabiasQue[0].icono}
											texto={sections[2].sabiasQue[0].texto}
										/>
									</Box>
								)}
							</Box>
						)}
				</Box>

				{/* Sección 3: Fisherman's Wharf (bloque inferior con imagen grande) */}
				{sections.length > 3 && sections[3].title && (
					<Box data-block-id="text-5" sx={{ position: "relative", marginTop: isSmallScreen ? 30 : 0 }}>
						<Box sx={{ width: "100%", position: "relative" }}>
							<Box sx={{ padding: "10px 12px", position: "relative" }}>
								{sections[3].sabiasQue?.[0] && (
									<Box
										sx={{
											position: "absolute",
											top: isSmallScreen ? -30 : 0,
											left: "50%",
										}}
									>
										<SabiasQue
											icono={sections[3].sabiasQue[0].icono}
											texto={sections[3].sabiasQue[0].texto}
										/>
									</Box>
								)}
								{sections[3].sabiasQue?.[1] && (
									<Box
										sx={{
											position: "absolute",
											top: isSmallScreen ? -30 : 0,
											left: "33%",
										}}
									>
										<SabiasQue
											icono={sections[3].sabiasQue[1].icono}
											texto={sections[3].sabiasQue[1].texto}
										/>
									</Box>
								)}
								{sections[3].sabiasQue?.[2] && (
									<Box
										sx={{
											position: "absolute",
											top: isSmallScreen ? -30 : 0,
											left: "66%",
										}}
									>
										<SabiasQue
											icono={sections[3].sabiasQue[2].icono}
											texto={sections[3].sabiasQue[2].texto}
										/>
									</Box>
								)}
								<Box component="h3" sx={estiloH3}>
									{sections[3].title}
								</Box>
								{sections[3].body && (
									<Box component="p" sx={estiloP}>
										{sections[3].body}
									</Box>
								)}
							</Box>
						</Box>
						{sections[3].imageId && getImageSrc(sections[3].imageId) && (
							<Box
								style={{
									position: "relative",
								}}
							>
								<Box
									component="img"
									src={getImageSrc(sections[3].imageId)}
									alt=""
									sx={{ width: "100%", height: 180, objectFit: "cover" }}
								/>
								{sections[3].stickerImageId &&
									getImageSrc(sections[3].stickerImageId) && (
										<Box
											component="img"
											src={getImageSrc(sections[3].stickerImageId)}
											alt=""
											sx={{
												width: 100,
												position: "absolute",
												bottom: 20,
												right: 20,
												zIndex: 2,
											}}
										/>
									)}
							</Box>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template170.thumbnail = "/images/template-1.png";
export default Template170;
